import axios from 'axios';
import createHttp from '@/config/http';
import type { AuditRecord, CreateAuditDto, CreateAuditResponse } from '@/types/audit';
import { generateRequestHeader } from '@/utils/axiosUtils';

/**
 * Dedicated axios instance for audit calls to avoid circular dependencies
 * Does NOT use the main http.ts config or interceptors
 */
const auditHttp = axios.create({
	baseURL: import.meta.env.VITE_COMMON_URL,
});

/**
 * Send audit data to backend
 * Returns the audit response with auditId for bg-process tracking
 */
export const createAudit = async (
	auditData: CreateAuditDto,
): Promise<CreateAuditResponse | undefined> => {
	try {
		const refHeader = await generateRequestHeader(1);

		const response = await auditHttp.post('/audit', auditData, {
			headers: {
				'x-ref-id': refHeader,
			},
		});
		
		return response.data;
	} catch {
		// Silent fail - audit should never break the user experience
		return undefined;
	}
};

/**
 * Fetch paginated audit records from backend
 * Used by Audit table component
 */
export const getAudits = async (
	page: number,
	limit: number,
	columnsFilters: string,
	search: string,
	status: string,
	globalFilters: string,
	sort: string,
): Promise<{ items: AuditRecord[]; meta: any }> => {
	const http = createHttp(import.meta.env.VITE_COMMON_URL);
	const response = await http.get('/audit', {
		params: {
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

/**
 * Fetch single audit record by ID
 * Used by Audit details modal
 */
export const getAudit = async (id: number): Promise<AuditRecord> => {
	const http = createHttp(import.meta.env.VITE_COMMON_URL);
	const response = await http.get(`/audit/${id}`);
	return response.data.data;
};
